import { useEffect, useRef } from 'react';
import { useRevealOnScroll } from '@/hooks/useRevealOnScroll';

export default function WhoWeAre() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, 1 - (rect.top / window.innerHeight)));
      section.style.setProperty('--scroll-progress', String(progress));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useRevealOnScroll(sectionRef);

  return (
    <section
      id="who"
      ref={sectionRef}
      className="relative w-full min-h-[80vh] md:min-h-screen flex items-center justify-center overflow-hidden bg-background-100"
    >
      {/* Section transition marker */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3 z-20">
        <div className="w-10 h-[1px] bg-gradient-to-r from-transparent to-accent-500/20"></div>
        <div className="w-1 h-1 rounded-full bg-accent-500/25"></div>
        <div className="w-10 h-[1px] bg-gradient-to-l from-transparent to-accent-500/20"></div>
      </div>

      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20warm%20mineral%20stone%20abstract%20composition%20with%20gentle%20golden%20mica%20luster%20and%20light%20rays%20emanating%20from%20upper%20center%20across%20slate%20grey%20and%20graphite%20tones%20deep%20shadowed%20basalt%20textures%20below%20organic%20marble-like%20swirls%20subtle%20crystalline%20shimmer%20sophisticated%20mineral%20aesthetic%20no%20text%20cinematic%20depth%20editorial%20moody%20atmosphere&width=1600&height=900&seq=who-bg-stone-01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top opacity-45"
        />
        {/* Gradient overlays for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-background-100 via-background-100/50 to-background-100"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-background-100/40 via-transparent to-background-100/40"></div>
      </div>

      {/* Floating amber particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[15%] left-[10%] w-1 h-1 rounded-full bg-accent-500/40 animate-float" style={{ animationDelay: '0s' }}></div>
        <div className="absolute top-[25%] right-[15%] w-1.5 h-1.5 rounded-full bg-accent-400/30 animate-float" style={{ animationDelay: '2s', animationDuration: '10s' }}></div>
        <div className="absolute bottom-[20%] left-[20%] w-1 h-1 rounded-full bg-accent-500/25 animate-float-slow" style={{ animationDelay: '4s' }}></div>
        <div className="absolute bottom-[30%] right-[10%] w-1.5 h-1.5 rounded-full bg-accent-400/35 animate-float" style={{ animationDelay: '1s', animationDuration: '9s' }}></div>
        <div className="absolute top-[60%] left-[5%] w-1 h-1 rounded-full bg-accent-500/20 animate-float" style={{ animationDelay: '3s' }}></div>
        <div className="absolute top-[40%] right-[5%] w-1 h-1 rounded-full bg-accent-400/25 animate-float-slow" style={{ animationDelay: '5s' }}></div>
      </div>

      {/* Ambient glow orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-accent-500/3 blur-[200px] animate-breathe"
          style={{ opacity: 'calc(0.3 + var(--scroll-progress, 0) * 0.4)', animationDuration: '12s' }}
        ></div>
        <div
          className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] rounded-full bg-accent-400/2 blur-[150px] animate-breathe"
          style={{ opacity: 'calc(0.2 + var(--scroll-progress, 0) * 0.3)', animationDuration: '8s', animationDelay: '3s' }}
        ></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-5xl mx-auto text-center">
          {/* Eyebrow label */}
          <div data-reveal className="flex items-center justify-center gap-3 mb-8">
            <div className="w-8 h-[1px] bg-accent-500/40"></div>
            <span className="text-xs font-label tracking-[0.3em] uppercase text-accent-400">
              About Generative Tech
            </span>
            <div className="w-8 h-[1px] bg-accent-500/40"></div>
          </div>

          {/* Main statement */}
          <p data-reveal data-reveal-delay="0.1" className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-[1.2] tracking-tight">
            A software development company for businesses that are{' '}
            <span className="text-shimmer">done configuring</span>{' '}
            around someone else's product.
          </p>

          {/* Separator */}
          <div data-reveal data-reveal-delay="0.2" className="mt-10 mb-10 flex items-center justify-center gap-4">
            <div className="w-12 h-[1px] bg-foreground-400/20"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-accent-500/40"></div>
            <div className="w-12 h-[1px] bg-foreground-400/20"></div>
          </div>

          {/* Supporting description */}
          <p data-reveal data-reveal-delay="0.3" className="font-body text-base md:text-lg text-foreground-700 max-w-3xl mx-auto leading-relaxed">
            We build custom software across platforms, develop AI and engineer the trading
            platforms and tools our clients run on — generated to fit, not bought off the shelf.
          </p>
        </div>
      </div>
    </section>
  );
}